import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Calendar, Users, School, ChevronDown, ChevronRight, Trophy, TrendingUp, TrendingDown, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArchiveManagement() {
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [currentMonthStats, setCurrentMonthStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveToDelete, setArchiveToDelete] = useState(null);
  const [expandedClasses, setExpandedClasses] = useState({});
  
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchArchives();
    fetchCurrentMonthStats();
  }, []);

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API}/admin/archives`, {
        headers: getAuthHeaders(),
      });
      setArchives(response.data);
    } catch (error) {
      console.error('Error fetching archives:', error);
    }
  };

  const fetchCurrentMonthStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/current-month-stats`, {
        headers: getAuthHeaders(),
      });
      setCurrentMonthStats(response.data);
    } catch (error) {
      console.error('Error fetching current month stats:', error);
    }
  };

  const fetchArchiveDetail = async (archiveId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/archives/${archiveId}`, {
        headers: getAuthHeaders(),
      });
      setArchiveDetail(response.data);
      setSelectedArchive(archiveId);
    } catch (error) {
      toast.error('Мұрағат деректерін жүктеу кезінде қате орын алды');
      console.error('Error fetching archive detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveAndReset = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/archives/archive-and-reset`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success(response.data.message);
      setArchiveDialogOpen(false);
      fetchArchives();
      fetchCurrentMonthStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
      console.error('Error archiving:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArchive = async () => {
    if (!archiveToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/archives/${archiveToDelete.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Мұрағат өшірілді');
      setDeleteDialogOpen(false);
      setArchiveToDelete(null);
      if (selectedArchive === archiveToDelete.id) {
        setSelectedArchive(null);
        setArchiveDetail(null);
      }
      fetchArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
      console.error('Error deleting archive:', error);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = {
      '01': 'Қаңтар', '02': 'Ақпан', '03': 'Наурыз', '04': 'Сәуір',
      '05': 'Мамыр', '06': 'Маусым', '07': 'Шілде', '08': 'Тамыз',
      '09': 'Қыркүйек', '10': 'Қазан', '11': 'Қараша', '12': 'Желтоқсан'
    };
    return `${months[month] || month} ${year}`;
  };

  const toggleClassExpand = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  // Group students by class for archive detail view
  const getStudentsByClass = () => {
    if (!archiveDetail?.student_scores) return {};
    
    const grouped = {};
    archiveDetail.student_scores.forEach(student => {
      if (!grouped[student.class_id]) {
        grouped[student.class_id] = {
          class_name: student.class_name,
          students: []
        };
      }
      grouped[student.class_id].students.push(student);
    });
    
    // Sort students by score within each class
    Object.keys(grouped).forEach(classId => {
      grouped[classId].students.sort((a, b) => b.total_score - a.total_score);
    });
    
    return grouped;
  };

  const studentsByClass = getStudentsByClass();

  return (
    <div className="space-y-6 fade-in">
      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Айды мұрағаттау және жаңарту</AlertDialogTitle>
            <AlertDialogDescription>
              Ағымдағы айдың ({currentMonthStats?.current_month}) барлық деректері мұрағатқа сақталады және жаңа ай басталады. 
              Барлық ұпайлар сіфірленеді. Бұл әрекетті қайтару мүмкін емес.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Болдырмау</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveAndReset} className="bg-primary hover:bg-primary/90">
              Мұрағаттау және жаңарту
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Archive Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Мұрағатты өшіру</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveToDelete && formatMonth(archiveToDelete.month)}" мұрағатын өшіргіңіз келетініне сенімдісіз бе? Бұл әрекетті қайтару мүмкін емес.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Болдырмау</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteArchive} className="bg-red-600 hover:bg-red-700">
              Өшіру
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold heading-font tracking-tight flex items-center gap-2">
            <Archive className="h-8 w-8 text-primary" />
            Мұрағат
          </h2>
          <p className="text-muted">Өткен айлардың деректерін қарау</p>
        </div>
        <Button 
          onClick={() => setArchiveDialogOpen(true)}
          disabled={currentMonthStats?.is_archived || loading}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Айды мұрағаттау және жаңарту
        </Button>
      </div>

      {/* Current Month Info */}
      {currentMonthStats && (
        <Card className="paper-card border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-primary" />
                <div>
                  <h3 className="font-semibold">Ағымдағы ай: {formatMonth(currentMonthStats.current_month)}</h3>
                  <p className="text-sm text-muted">
                    {currentMonthStats.record_count} жазба | 
                    {currentMonthStats.is_archived ? ' ✅ Мұрағатталған' : ' ⏳ Мұрағатталмаған'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Archives List */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-semibold text-lg">Мұрағаттар тізімі</h3>
          
          {archives.length === 0 ? (
            <Card className="paper-card">
              <CardContent className="py-12 text-center text-muted">
                <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Мұрағаттар жоқ</p>
                <p className="text-sm mt-2">Ай аяқталғанда мұрағаттаңыз</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {archives.map(archive => (
                <Card 
                  key={archive.id} 
                  className={`paper-card cursor-pointer transition-all hover:shadow-md ${selectedArchive === archive.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => fetchArchiveDetail(archive.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{formatMonth(archive.month)}</h4>
                        <p className="text-sm text-muted">
                          {archive.class_count} сынып, {archive.student_count} оқушы, {archive.teacher_count || 0} мұғалім
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setArchiveToDelete(archive);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Archive Detail */}
        <div className="lg:col-span-2">
          {loading ? (
            <Card className="paper-card">
              <CardContent className="py-12 text-center text-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
                <p>Жүктелуде...</p>
              </CardContent>
            </Card>
          ) : archiveDetail ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">
                {formatMonth(archiveDetail.month)} - Толық есеп
              </h3>

              <Tabs defaultValue="classes" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="classes">Сыныптар рейтингі</TabsTrigger>
                  <TabsTrigger value="students">Оқушылар бойынша</TabsTrigger>
                  <TabsTrigger value="teachers">Мұғалімдер</TabsTrigger>
                  <TabsTrigger value="top10">Үздік 10</TabsTrigger>
                </TabsList>

                {/* Classes Ranking Tab */}
                <TabsContent value="classes" className="space-y-3">
                  {archiveDetail.class_averages
                    .sort((a, b) => b.average_score - a.average_score)
                    .map((cls, index) => (
                      <Card key={cls.class_id} className="paper-card">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                index === 0 ? 'bg-yellow-500' : 
                                index === 1 ? 'bg-gray-400' : 
                                index === 2 ? 'bg-amber-600' : 'bg-primary/60'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-semibold">{cls.class_name}</h4>
                                <p className="text-sm text-muted">{cls.student_count} оқушы</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${cls.average_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {cls.average_score >= 0 ? '+' : ''}{cls.average_score}
                              </div>
                              <p className="text-xs text-muted">орташа ұпай</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </TabsContent>

                {/* Students by Class Tab */}
                <TabsContent value="students" className="space-y-3">
                  {Object.entries(studentsByClass).map(([classId, classData]) => (
                    <Card key={classId} className="paper-card">
                      <Collapsible open={expandedClasses[classId]} onOpenChange={() => toggleClassExpand(classId)}>
                        <CollapsibleTrigger asChild>
                          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3 px-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {expandedClasses[classId] ? (
                                  <ChevronDown className="h-5 w-5 text-primary" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted" />
                                )}
                                <School className="h-5 w-5 text-primary" />
                                <div>
                                  <CardTitle className="text-lg">{classData.class_name}</CardTitle>
                                  <p className="text-sm text-muted">{classData.students.length} оқушы</p>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent className="pt-0 px-4 pb-4">
                            <div className="space-y-2">
                              {classData.students.map((student, idx) => (
                                <div key={student.student_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                  <div className="flex items-center gap-3">
                                    <span className="w-6 text-center text-sm text-muted">{idx + 1}</span>
                                    <span className="font-medium">{student.student_name}</span>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1 text-green-600">
                                      <TrendingUp className="h-4 w-4" />
                                      <span className="text-sm">{student.merit_count}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-red-600">
                                      <TrendingDown className="h-4 w-4" />
                                      <span className="text-sm">{student.demerit_count}</span>
                                    </div>
                                    <div className={`font-bold ${student.total_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {student.total_score >= 0 ? '+' : ''}{student.total_score}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  ))}
                </TabsContent>

                {/* Top 10 Tab */}
                {/* Teachers Tab */}
                <TabsContent value="teachers" className="space-y-3">
                  <Card className="paper-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Мұғалімдер статистикасы
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {archiveDetail.teacher_stats && archiveDetail.teacher_stats.length > 0 ? (
                        <div className="space-y-3">
                          {archiveDetail.teacher_stats
                            .filter(t => t.total_records > 0)
                            .sort((a, b) => b.total_records - a.total_records)
                            .map((teacher, index) => (
                              <div key={teacher.teacher_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                    index === 0 ? 'bg-yellow-500' : 
                                    index === 1 ? 'bg-gray-400' : 
                                    index === 2 ? 'bg-amber-600' : 'bg-primary/60'
                                  }`}>
                                    {index + 1}
                                  </div>
                                  <div>
                                    <span className="font-medium">{teacher.teacher_name}</span>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs text-green-600">{teacher.merit_count} мақтау</span>
                                      <span className="text-xs text-red-600">{teacher.demerit_count} сөгіс</span>
                                      <span className="text-xs text-muted">{teacher.students_served} оқушы</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xl font-bold text-primary">{teacher.total_records}</div>
                                  <p className="text-xs text-muted">жазба</p>
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-center text-muted py-4">Мұғалімдер деректері жоқ</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="top10" className="space-y-3">
                  <Card className="paper-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-yellow-500" />
                        Үздік 10 оқушы
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {archiveDetail.student_scores
                          .sort((a, b) => b.total_score - a.total_score)
                          .slice(0, 10)
                          .map((student, index) => (
                            <div key={student.student_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                  index === 0 ? 'bg-yellow-500' : 
                                  index === 1 ? 'bg-gray-400' : 
                                  index === 2 ? 'bg-amber-600' : 'bg-primary/60'
                                }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <span className="font-medium">{student.student_name}</span>
                                  <p className="text-xs text-muted">{student.class_name}</p>
                                </div>
                              </div>
                              <div className={`text-xl font-bold ${student.total_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {student.total_score >= 0 ? '+' : ''}{student.total_score}
                              </div>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <Card className="paper-card">
              <CardContent className="py-12 text-center text-muted">
                <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Мұрағатты таңдаңыз</p>
                <p className="text-sm mt-2">Сол жақтағы тізімнен айды таңдаңыз</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
